/** Companies — list view (server-side pagination/sort/search + delete). */
import * as React from "react";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useGo, useNavigation } from "@refinedev/core";
import type { Company } from "@aos/shared";

import { StatusBadge } from "@/components/app/StatusBadge";

import { ResourceListPage, RowActions } from "../_shared/ListScaffold";

export function CompanyList() {
  const go = useGo();
  const { editUrl, showUrl } = useNavigation();
  const [pendingDelete, setPendingDelete] = React.useState<Company | null>(
    null,
  );

  const columns = useMemo<ColumnDef<Company, unknown>[]>(
    () => [
      { id: "name", accessorKey: "name", header: "Name" },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) =>
          new Date(row.original.created_at).toLocaleDateString(),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions
            onShow={() => go({ to: showUrl("companies", row.original.id) })}
            onEdit={() => go({ to: editUrl("companies", row.original.id) })}
            onDelete={() => setPendingDelete(row.original)}
          />
        ),
      },
    ],
    [go, editUrl, showUrl],
  );

  return (
    <ResourceListPage<Company>
      resource="companies"
      title="Companies"
      description="The organisations that own projects and work."
      createLabel="New Company"
      columns={columns}
      searchPlaceholder="Search companies…"
      emptyMessage="No companies yet. Create your first company."
      deleteEntityLabel="company"
      deleteName={(c) => c.name}
      pendingDelete={pendingDelete}
      setPendingDelete={setPendingDelete}
    />
  );
}

export default CompanyList;
